import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import AuthCallback from "@/components/AuthCallback";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import KanbanPage from "@/pages/KanbanPage";
import TablePage from "@/pages/TablePage";
import TaskDetailPage from "@/pages/TaskDetailPage";
import CreateTaskPage from "@/pages/CreateTaskPage";
import AdminPage from "@/pages/AdminPage";
import FeatureRequestPage from "@/pages/FeatureRequestPage";
import PDPVariationsPage from "@/pages/PDPVariationsPage";
import WebsiteSpeedPage from "@/pages/WebsiteSpeedPage";
import ABTestingPage from "@/pages/ABTestingPage";
import ABTestDetailPage from "@/pages/ABTestDetailPage";
import CreateABTestPage from "@/pages/CreateABTestPage";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="animate-pulse text-[#555555] text-sm">Loading...</div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();

  // CRITICAL: Detect session_id in hash synchronously during render, before routes.
  // This prevents race conditions with ProtectedRoute checking auth before callback completes.
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<DashboardPage />} />
        <Route path="kanban" element={<KanbanPage />} />
        <Route path="tasks" element={<TablePage />} />
        <Route path="tasks/new" element={<CreateTaskPage />} />
        <Route path="tasks/:id" element={<TaskDetailPage />} />
        <Route path="ab-tests" element={<ABTestingPage />} />
        <Route path="ab-tests/new" element={<CreateABTestPage />} />
        <Route path="ab-tests/:id" element={<ABTestDetailPage />} />
        <Route path="feature-request" element={<FeatureRequestPage />} />
        <Route path="pdp-variations" element={<PDPVariationsPage />} />
        <Route path="website-speed" element={<WebsiteSpeedPage />} />
        <Route path="admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="bottom-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
